// **The ambient loop → renderer adapter** (`client-home`; plan **D14**).
//
// The shipped PixiJS `CanvasRenderer` does not want a session, a socket or a trace — it wants a
// `WorldSnapshot`, a `Camera` and a `RenderInput`, all of them plain data. So the whole ambient
// background is this pure function plus a canvas host, and **`CanvasRenderer` is not modified**:
// the same argument that closed **D-156** for the review screen.
//
// What the mapping has to decide, and why:
//
//   * **Facing.** The format carries no facing byte, and neither does the wire (a constant 0
//     since D-063) — the renderer's `FacingDeriver` derives heading from successive positions.
//     Feeding it successive ambient frames gives the same result by the same path, which is why
//     the format carries stable bee **ids**.
//   * **The queen.** The loop records worker bees only. The renderer draws the own colony's queen
//     from `RenderInput.ownHiveAnchor` as the `own-queen` anchor sprite (D-091), so she arrives
//     via the anchor rather than as a bee — emitting one would draw two queens on one hive.
//   * **Patches.** The loop records positions, not pools. Every patch is drawn in bloom
//     (`pool > 0`): this is a summer backdrop, and a dead-flower sheet on a menu would read as
//     something being wrong.
//   * **Truncation.** Always `false` in both flags. An ambient frame is the whole loop by
//     construction, never a viewport-capped payload, and reporting a cap that did not happen
//     would drop the renderer into `reduced` animation for no reason.

import type { WorldSnapshot } from '../../net/wire/connection';
import type { BeeRecord, CellRecord, PatchRecord } from '../../net/wire/messages';
import { ActionClass, Season, Tier } from '../../net/wire/messages';
import type { Camera } from '../../canvas/projection';
import type { RenderInput } from '../../canvas/render-input';
import type { AmbientLoop } from './ambient-format';

/** The colony the loop records. A backdrop is one colony going about its business, so the viewer
 * is always "its own" colony — which is what gives the bees their type colours rather than the
 * enemy-band treatment. */
export const AMBIENT_COLONY = 0;

/** A nominal per-bee health/nectar. The loop records neither: the bars are a play-screen
 * affordance and a backdrop has no reason to carry per-bee telemetry, so every bee draws whole. */
const FULL = 255;

/** A nominal, top-bucket cell hp (`combat-strikes-and-damage-flash`). The loop's cells are
 * always drawn intact (`damaged: false`), so their raw hp bucket is the full 31, consistent
 * with `FULL` above for bees/patches — a decorative backdrop has no damage to carry. */
const FULL_BUCKET = 31;

/**
 * The `WorldSnapshot` for a frame index, wrapping modulo the loop length — the loop is a loop,
 * so an index past the end is the beginning rather than an error.
 */
export function ambientWorldSnapshot(loop: AmbientLoop, frameIndex: number): WorldSnapshot | null {
  if (loop.frames.length === 0) {
    return null;
  }
  const index = ((frameIndex % loop.frames.length) + loop.frames.length) % loop.frames.length;
  const frame = loop.frames[index]!;

  const bees = new Map<number, BeeRecord>();
  for (const bee of frame.bees) {
    bees.set(bee.id, {
      id: bee.id,
      position: [bee.position[0], bee.position[1]],
      // No facing byte in either transport; the renderer derives heading from successive frames.
      facing: 0,
      action: ActionClass.Forage,
      gathering: false,
      colonyId: AMBIENT_COLONY,
      typeId: bee.typeId,
      health: FULL,
      nectar: 0,
    });
  }

  const patches = new Map<number, PatchRecord>();
  loop.patches.forEach((point, id) => {
    patches.set(id, { id, position: [point[0], point[1]], pool: FULL });
  });

  const cells = new Map<number, CellRecord>();
  loop.cells.forEach((point, id) => {
    cells.set(id, {
      id,
      position: [point[0], point[1]],
      occupied: true,
      damaged: false,
      ruin: false,
      hpBucket: FULL_BUCKET,
    });
  });

  return {
    tier: Tier.Close,
    tick: index,
    seq: BigInt(index),
    bees,
    blobs: new Map(),
    queens: new Map(),
    patches,
    ruins: new Map(),
    dances: new Map(),
    danceRadius: 0,
    cells,
    cellsTruncated: false,
    truncated: false,
    totalCount: bees.size,
  };
}

/** The hive anchor: the centroid of the loop's recorded cells, or the field centre when it
 * records none. This is what draws the hive tile and the queen. */
export function ambientHiveAnchor(loop: AmbientLoop): [number, number] {
  if (loop.cells.length === 0) {
    return [0, 0];
  }
  let sumX = 0;
  let sumY = 0;
  for (const [x, y] of loop.cells) {
    sumX += x;
    sumY += y;
  }
  return [sumX / loop.cells.length, sumY / loop.cells.length];
}

/**
 * The render-input context. Fixed at summer: the loop is a single seasonal moment rather than a
 * whole match, and a menu backdrop that visibly died back over 30 seconds would read as the game
 * failing rather than as atmosphere.
 *
 * `playhead` carries the frame index so a **wrap** — index N back to 0 — resets the renderer's
 * interpolation history instead of tweening every bee across the field in one frame. That is
 * exactly what the field is for, and without it the loop's seam would be a visible snap.
 */
export function ambientRenderInput(loop: AmbientLoop, frameIndex: number): RenderInput {
  return {
    season: Season.Summer,
    ownHiveAnchor: ambientHiveAnchor(loop),
    ownColonyLifecycle: 'live',
    ownColonyId: AMBIENT_COLONY,
    playhead: loop.frames.length === 0 ? 0 : frameIndex % loop.frames.length,
    connectionEpoch: 0,
    streamEpoch: 0,
  };
}

/**
 * The camera: the hive centred in the measured viewport at a close-enough scale that individual
 * bees read as bees.
 *
 * `Camera.offset` is the world point mapped to the screen ORIGIN, not a look-at centre — the
 * distinction that caused the blank-field bug when it was first got wrong — so centring means
 * subtracting half the viewport in world units.
 */
export function ambientCamera(
  loop: AmbientLoop,
  viewport: { width: number; height: number },
  scale = 0.75,
): Camera {
  const [hiveX, hiveY] = ambientHiveAnchor(loop);
  return {
    offsetX: hiveX - viewport.width / 2 / scale,
    offsetY: hiveY - viewport.height / 2 / scale,
    scale,
  };
}
